import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { DataGrid, GridEditInputCell, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, useGridApiRef } from '@mui/x-data-grid';
import { apiService } from '../services/apiService';
import ShareBOM from './ShareBOM';
import { filterRows } from '../services/bomUtils';
import { applyDrafts, canEditRow, changedCells, draftBatches } from '../services/bomDrafts';
import { DescriptionOutlined, DriveFileRenameOutline, UploadFileOutlined } from '@mui/icons-material';
import QuoteComparison from './QuoteComparison';
import { ExpandedRowContext, ExpandableBOMRow, EXPANDED_ROW_HEIGHT } from './ExpandableBOMRow';
import PartMappingFields from './PartMappingFields';
import MappedPartDetail from './MappedPartDetail';
import { emptyMapping, groupPartRows, validMapping } from '../services/partMapping';

const message = error => error.response?.data?.error || error.message || 'Request failed.';

function Toolbar() {
  return <GridToolbarContainer><GridToolbarColumnsButton /><GridToolbarFilterButton /></GridToolbarContainer>;
}

export default function BOMEditor() {
  const { bomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [bom, setBom] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [filterColumn, setFilterColumn] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [share, setShare] = useState(false);
  const [columnDialog, setColumnDialog] = useState(false);
  const [columnName, setColumnName] = useState('');
  const [deleteColumn, setDeleteColumn] = useState('');
  const [exportFiltered, setExportFiltered] = useState(false);
  const [gridFilter, setGridFilter] = useState({ items: [] });
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [detailsName, setDetailsName] = useState('');
  const [detailsDescription, setDetailsDescription] = useState('');
  const [detailsMapping, setDetailsMapping] = useState(null);
  const [sourceView, setSourceView] = useState(false);
  const [drafts, setDrafts] = useState({});
  const draftsRef = useRef({});
  const savingRef = useRef(false);
  const activeCellRef = useRef(null);
  const [editingCell, setEditingCell] = useState(false);
  const [editableOnly, setEditableOnly] = useState(false);
  const [notice, setNotice] = useState('');
  const [expanded, setExpanded] = useState(null);
  const getRowHeight = useCallback(({ id }) => id === expanded ? EXPANDED_ROW_HEIGHT : 34, [expanded]);
  const apiRef = useGridApiRef();
  const hasDrafts = Object.keys(drafts).length > 0;
  const unsaved = hasDrafts || editingCell;
  const setPending = useCallback(value => {
    draftsRef.current = value;
    setDrafts(value);
  }, []);

  const load = useCallback(async () => {
    const data = await apiService.getBOM(bomId);
    setBom(data);
    return data;
  }, [bomId]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    setBom(null); setError('');
    setPending({}); setNotice(''); setEditableOnly(false);
    setExpanded(null); setSourceView(false);
    activeCellRef.current = null; setEditingCell(false);
    const poll = async () => {
      try {
        const data = await apiService.getBOM(bomId);
        if (cancelled) return;
        setBom(data);
        if (!['ready', 'failed'].includes(data.status)) timer = setTimeout(poll, 2000);
      } catch (failure) { if (!cancelled) setError(message(failure)); }
    };
    const init = async () => {
      const token = new URLSearchParams(location.hash.slice(1)).get('share');
      try {
        if (token) {
          await apiService.sharing(bomId, 'redeem', { token });
          if (!cancelled) navigate(location.pathname, { replace: true });
        } else await poll();
      } catch (failure) { if (!cancelled) setError(message(failure)); }
    };
    init();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bomId, location.hash, location.pathname, navigate, setPending]);

  useEffect(() => {
    if (!unsaved && !savingRef.current) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    const confirmNavigation = event => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey
          || event.shiftKey || event.altKey || anchor.target === '_blank' || anchor.hasAttribute('download')
          || anchor.href === window.location.href) return;
      if (savingRef.current || !window.confirm('Leave this BOM and discard unsaved cell edits?')) {
        event.preventDefault(); event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', warn);
    document.addEventListener('click', confirmNavigation, true);
    return () => {
      window.removeEventListener('beforeunload', warn);
      document.removeEventListener('click', confirmNavigation, true);
    };
  }, [unsaved, busy]);

  const stageRow = next => {
    const original = bom.items.find(row => row.line_item_id === next.line_item_id);
    if (!original || bom.status !== 'ready' || !canEditRow(original, bom.policy)) throw new Error('This row is read-only.');
    const patch = changedCells(original, next, bom.policy.write_columns);
    const updated = { ...draftsRef.current };
    if (Object.keys(patch).length) updated[next.line_item_id] = patch;
    else delete updated[next.line_item_id];
    setPending(updated); setNotice('');
    return { ...original, data: { ...original.data, ...patch } };
  };

  const finishActiveCell = discard => {
    const cell = activeCellRef.current;
    if (cell && apiRef.current.getCellMode(cell.id, cell.field) === 'edit') {
      if (!discard) stageRow(apiRef.current.getRowWithUpdatedValues(cell.id, cell.field));
      apiRef.current.stopCellEditMode({ ...cell, ignoreModifications: true });
    }
    activeCellRef.current = null; setEditingCell(false);
  };

  const saveDrafts = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setError(''); setNotice('');
    let savedRows = 0;
    try {
      finishActiveCell(false);
      // Let a pending grid focus-out update finish before taking the snapshot.
      await new Promise(resolve => setTimeout(resolve, 0));
      const batches = draftBatches(draftsRef.current, bom.policy.max_batch_rows || 99);
      if (!batches.length) return;
      setBusy(true);
      let current = bom;
      for (const items of batches) {
        const result = await apiService.mutateBOM(bomId, current.revision, 'batch_update', { items });
        const saved = Object.fromEntries(items.map(item => [item.line_item_id, item.data]));
        current = { ...current, revision: result.revision, items: applyDrafts(current.items, saved) };
        setBom(current);
        const remaining = { ...draftsRef.current };
        items.forEach(item => { delete remaining[item.line_item_id]; });
        setPending(remaining);
        savedRows += items.length;
      }
      setNotice(`Saved changes to ${savedRows} row${savedRows === 1 ? '' : 's'}.`);
      try { await load(); }
      catch (failure) { setError(`Changes were saved, but refreshing failed: ${message(failure)}`); }
    } catch (failure) {
      setError(`${message(failure)} ${savedRows ? `${savedRows} rows were saved. ` : ''}Unsaved edits are kept. Reloading will ask before discarding them.`);
    } finally { savingRef.current = false; setBusy(false); }
  };

  const discardDrafts = () => {
    if (!window.confirm('Discard all unsaved cell edits?')) return;
    finishActiveCell(true); setPending({}); setError(''); setNotice('');
  };

  const reload = async () => {
    if (unsaved && !window.confirm('Reload this BOM and discard unsaved cell edits?')) return;
    setBusy(true);
    try {
      const data = await apiService.getBOM(bomId);
      finishActiveCell(true); setPending({}); setBom(data); setError(''); setNotice('');
    } catch (failure) { setError(message(failure)); }
    finally { setBusy(false); }
  };

  const perform = async (operation, data = {}) => {
    if (Object.keys(draftsRef.current).length || activeCellRef.current) {
      setError('Save or discard your cell edits before changing BOM structure.');
      return false;
    }
    setBusy(true); setError('');
    try {
      await apiService.mutateBOM(bomId, bom.revision, operation, data);
      await load();
      return true;
    } catch (failure) { setError(message(failure)); return false; }
    finally { setBusy(false); }
  };

  const draftRows = useMemo(() => applyDrafts(bom?.items || [], drafts), [bom, drafts]);
  const visibleRows = useMemo(() => {
    const rows = editableOnly && bom ? draftRows.filter(row => canEditRow(row, bom.policy)) : draftRows;
    return filterRows(rows, bom?.columns || [], { search, column: filterColumn, value: filterValue, gridFilter });
  }, [bom, draftRows, editableOnly, search, filterColumn, filterValue, gridFilter]);
  const grouped = !!bom?.part_mapping?.secondary_column && !sourceView;
  const displayRows = useMemo(() => grouped
    ? groupPartRows(visibleRows, bom.part_mapping, bom.columns, bom.items) : visibleRows,
  [grouped, visibleRows, bom]);

  const download = async format => {
    if (unsaved) { setError('Save or discard your cell edits before exporting.'); return; }
    setBusy(true); setError('');
    try {
      const result = await apiService.exportBOM({
        bom_id: bomId, format,
        ...(exportFiltered ? { row_ids: visibleRows.map(r => r.line_item_id) } : {})
      });
      const anchor = document.createElement('a');
      anchor.href = result.url;
      anchor.rel = 'noreferrer';
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
    } catch (failure) { setError(message(failure)); }
    finally { setBusy(false); }
  };

  if (!bom) return <div>
    {error ? <Alert variant="danger">{error}<div><Button onClick={() => load().then(() => setError('')).catch(e => setError(message(e)))}>Retry</Button></div></Alert>
      : <Spinner animation="border" role="status"><span className="visually-hidden">Loading BOM</span></Spinner>}
  </div>;

  const policy = bom.policy;
  const ready = bom.status === 'ready';
  const showPermissions = !policy.manage_access;
  const writableColumns = new Set(policy.write_columns);
  const rowEditable = row => ready && canEditRow(row, policy);
  const cellEditable = (row, field) => !grouped && field.startsWith('cell:') && rowEditable(row) && writableColumns.has(field.slice(5));
  const editableColumns = bom.columns.filter(column => writableColumns.has(column.id));
  const readOnlyColumns = bom.columns.filter(column => !writableColumns.has(column.id));
  const editableRowCount = bom.items.filter(rowEditable).length;
  const visibleEditableRowCount = visibleRows.filter(rowEditable).length;
  const cellAccessLabel = (row, column) => {
    if (grouped) return 'Expand this primary part to view original source fields and their editing access.';
    if (!writableColumns.has(column.id)) return 'Read-only: your role cannot edit this column.';
    if (!rowEditable(row)) return 'Read-only: this row is outside your editable scope.';
    return 'Editable: double-click to edit. Press Enter to keep the draft, then click Save changes.';
  };
  const startRowEdit = row => {
    if (busy || !rowEditable(row)) return;
    finishActiveCell(false);
    const column = apiRef.current.getVisibleColumns().find(candidate =>
      candidate.field.startsWith('cell:') && writableColumns.has(candidate.field.slice(5)));
    if (!column) {
      setError('Show an editable column using Columns before editing this row.');
      return;
    }
    apiRef.current.setCellFocus(row.line_item_id, column.field);
    apiRef.current.startCellEditMode({ id: row.line_item_id, field: column.field });
    activeCellRef.current = { id: row.line_item_id, field: column.field };
    setEditingCell(true);
  };
  const columns = [
    { field: '__order', headerName: '#', width: 92, filterable: false, hideable: false,
      valueGetter: (_, row) => row.order_index + 1,
      renderCell: ({ row }) => <div className="d-flex align-items-center gap-1">
        <button type="button" className="bomwise-expand-toggle" aria-label={`${expanded === row.line_item_id ? 'Collapse' : 'Expand'} ${grouped ? 'MPNs' : 'quotes'} for row ${row.order_index + 1}`}
          aria-expanded={expanded === row.line_item_id} disabled={busy || (!grouped && row.quote_access === false) || !policy.read_columns.length}
          onClick={() => setExpanded(previous => previous === row.line_item_id ? null : row.line_item_id)}>
          {expanded === row.line_item_id ? '▾' : '▸'}</button>{row.order_index + 1}
      </div> },
    ...(showPermissions ? [{
      field: '__access', headerName: 'Row access', width: 145, sortable: false, filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => grouped ? <Badge
        bg={row.sourceRows.some(rowEditable) ? 'success' : 'secondary'}
        title="Expand to see editing access for each original source line.">
        {row.sourceRows.filter(rowEditable).length} / {row.sourceRows.length} editable lines
      </Badge> : <Badge bg={rowEditable(row) ? 'success' : 'secondary'}
        title={rowEditable(row) ? 'Only columns marked editable can be changed in this row.' : 'No cells in this row can be changed.'}
        aria-label={`Row ${row.order_index + 1}: ${rowEditable(row) ? 'Editable columns only' : 'Read-only'}`}>
        {rowEditable(row) ? 'Editable columns' : 'Read-only'}
      </Badge>
    }] : []),
    ...bom.columns.map(column => ({
      field: `cell:${column.id}`, headerName: column.name,
      width: /description|notes/i.test(column.name) ? 280 : /qty|quantity|unit|revision/i.test(column.name) ? 120 : 190,
      minWidth: 100,
      editable: !busy && !grouped && policy.write_columns.includes(column.id),
      renderEditCell: params => <GridEditInputCell {...params} debounceMs={0} />,
      ...(showPermissions ? {
        renderHeader: () => <div className="bomwise-permission-header" title={`${column.name}: ${
          writableColumns.has(column.id) ? (policy.row_restricted ? 'Editable in allowed rows only' : 'Editable') : 'Read-only'
        }`}>
          <span className="text-truncate">{column.name}</span>
          <small className={writableColumns.has(column.id) ? 'bomwise-permission-editable-label' : 'text-muted'}>
            {writableColumns.has(column.id) ? (grouped ? 'Edit in source fields' : policy.row_restricted ? 'Editable in allowed rows' : 'Editable') : 'Read-only'}
          </small>
        </div>,
        renderCell: ({ row }) => <span className="bomwise-permission-cell-content"
          title={cellAccessLabel(row, column)}
          aria-label={`${column.name}, row ${row.order_index + 1}: ${String(row.data[column.id] ?? '')}. ${cellAccessLabel(row, column)}`}>
          {String(row.data[column.id] ?? '')}
        </span>
      } : {}),
      ...(grouped && column.id === bom.part_mapping.primary_column ? {
        renderCell: ({ row }) => <button type="button" className="btn btn-link p-0 text-start text-truncate"
          disabled={busy} aria-expanded={expanded === row.line_item_id}
          onClick={() => setExpanded(previous => previous === row.line_item_id ? null : row.line_item_id)}>
          {String(row.data[column.id] ?? '').trim() || '(Missing primary part number)'}
          <small className="ms-2">({row.sourceRows.length} source {row.sourceRows.length === 1 ? 'line' : 'lines'})</small>
        </button>
      } : {}),
      valueGetter: (_, row) => row.data[column.id] ?? '',
      valueSetter: (value, row) => ({ ...row, data: { ...row.data, [column.id]: value } })
    })),
    ...(!grouped && editableColumns.length ? [{
      field: '__edit', headerName: 'Edit', width: 64, sortable: false, filterable: false,
      disableColumnMenu: true, hideable: false,
      renderCell: ({ row }) => <Button size="sm" variant="link" className="bomwise-row-pencil"
        aria-label={`Edit row ${row.order_index + 1}`} title="Edit the first writable cell in this row"
        disabled={busy || !rowEditable(row)} onClick={() => startRowEdit(row)}>
        <DriveFileRenameOutline fontSize="small" />
      </Button>
    }] : []),
    ...(!grouped && policy.reorder_rows ? [{
      field: '__actions', headerName: 'Order', width: 130, sortable: false, filterable: false,
      renderCell: ({ row }) => {
        const index = bom.items.findIndex(r => r.line_item_id === row.line_item_id);
        const move = direction => {
          const other = bom.items[index + direction];
          perform('reorder', { reorder_map: {
            [row.line_item_id]: other.order_index, [other.line_item_id]: row.order_index
          } });
        };
        return <div className="d-flex gap-1">
          <Button size="sm" variant="outline-secondary" aria-label={`Move row ${index + 1} up`} disabled={busy || unsaved || index === 0} onClick={() => move(-1)}>↑</Button>
          <Button size="sm" variant="outline-secondary" aria-label={`Move row ${index + 1} down`} disabled={busy || unsaved || index === bom.items.length - 1} onClick={() => move(1)}>↓</Button>
        </div>;
      }
    }] : [])
  ];

  return <div className="bomwise-workspace">
    <nav className="bomwise-breadcrumb" aria-label="Breadcrumb"><Link to="/projects">Projects</Link><span>/</span>
      {bom.project && <><Link to={`/projects/${encodeURIComponent(bom.project.project_id)}`}>{bom.project.name}</Link><span>/</span></>}
      <span>{bom.name}</span></nav>
    <div className="bomwise-page-heading">
      <div><div className="bomwise-eyebrow">BOM WORKSPACE</div><h1>{bom.name}</h1>
        <p className="text-muted mb-0">Manage your parts, collaborate securely, and keep every detail in view.</p>
        {!policy.manage_access && <p className="text-muted mb-0">Owned by {bom.owner_email || 'another account'}</p>}
        {bom.description && <p className="mt-2 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{bom.description}</p>}</div>
      <div className="d-flex flex-wrap gap-2">
        <Button variant="outline-secondary" disabled={busy} onClick={reload}>Reload</Button>
        {policy.manage_access && <Button disabled={busy || unsaved} onClick={() => setShare(true)}>Share</Button>}
        {policy.manage_access && <Button variant="outline-primary" disabled={busy || unsaved || !['ready', 'failed'].includes(bom.status)} onClick={() => {
          setDetailsName(bom.name); setDetailsDescription(bom.description || '');
          setDetailsMapping(bom.part_mapping || null); setDetailsDialog(true);
        }}>Edit details</Button>}
      </div>
    </div>
    <div className="bomwise-upload-strip">
      <UploadFileOutlined className="bomwise-upload-icon" />
      <div><strong>{bom.project ? 'Add another BOM to this project' : 'Start with another BOM'}</strong><span>Upload CSV or Excel using the existing preview and import workflow.</span></div>
      <Button as={Link} to={bom.project ? `/upload?project=${encodeURIComponent(bom.project.project_id)}` : '/upload'} variant="outline-primary" size="sm">Upload BOM</Button>
    </div>
    {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error} If another collaborator made changes, reload before trying again.</Alert>}
    {notice && <Alert variant="success" role="status">{notice}</Alert>}
    {hasDrafts && <Alert variant="warning" role="status">
      Unsaved changes in {Object.keys(drafts).length} row{Object.keys(drafts).length === 1 ? '' : 's'}. Click Save changes to send them together.
      Save or discard these edits before changing structure, sharing, editing details, or exporting.
    </Alert>}
    {bom.status === 'failed' && <Alert variant="danger">{bom.error}</Alert>}
    {!ready && bom.status !== 'failed' && <Alert variant="info"><Spinner size="sm" className="me-2" />Processing your upload…</Alert>}
    {ready && <>
      <section className="bomwise-worksheet" aria-label="Bill of materials">
        <div className="bomwise-sheet-heading">
          <DescriptionOutlined />
          <div><h2>Bill of materials</h2><span>Original line identities are preserved across sorting and filtering.</span></div>
          <Badge bg={policy.manage_access ? 'primary' : 'secondary'}>{policy.role}</Badge>
        </div>
        <div className="bomwise-workspace-stats" aria-label="BOM summary">
          <div><span>Line items</span><strong>{bom.items.length.toLocaleString()}</strong></div>
          <div><span>Visible columns</span><strong>{bom.columns.length}<small> / 100</small></strong></div>
          <div><span>Revision</span><strong>{bom.revision}</strong></div>
          <div><span>Save status</span><strong className={unsaved ? 'text-warning-emphasis' : 'text-success'}>{unsaved ? 'Unsaved edits' : 'No pending edits'}</strong></div>
        </div>
      {showPermissions && <details className="bomwise-access-details">
        <summary>Your editing access · {editableRowCount ? `${editableRowCount} editable rows` : 'Read-only'}</summary>
        <Card as="section" aria-label="Your editing access" className="border-0 shadow-none"><Card.Body>
        <h5>Your editing access</h5>
        <p className="mb-2">
          {editableRowCount > 0
            ? `${editableColumns.length} of ${bom.columns.length} visible columns can be edited in ${editableRowCount} of ${bom.items.length} rows.`
            : 'No cells are currently editable in this BOM.'}
        </p>
        {!!editableColumns.length && <p className="mb-2"><strong>Editable columns{policy.row_restricted ? ' (allowed rows only)' : ''}:</strong>{' '}
          {editableColumns.map(column => column.name).join(', ')}</p>}
        {!!readOnlyColumns.length && <p className="mb-2"><strong>Read-only columns:</strong>{' '}
          {readOnlyColumns.map(column => column.name).join(', ')}</p>}
        <p className="mb-0 text-muted">
          Highlighted cells are editable; muted cells are read-only. Check the Row access badges and column labels, or hover over a cell for details.
          {policy.row_restricted && ' Row permissions come from the owner’s sharing rules; search and display filters do not change your access.'}
        </p>
        {policy.row_restricted && <p className="mt-2 mb-0">You can edit only rows allowed by your role. Other rows are read-only. Structural changes are disabled.</p>}
      </Card.Body></Card></details>}
      <div className="bomwise-worksheet-toolbar">
        <strong>Parts <span className="bomwise-count">{displayRows.length}</span></strong>
        <Form.Control size="sm" className="bomwise-worksheet-search" aria-label="Search BOM" placeholder="Search parts and visible fields…" value={search} onChange={e => setSearch(e.target.value)} />
        {policy.add_rows && <Button size="sm" disabled={busy || unsaved} onClick={() => perform('add_row')}>Add row</Button>}
        <Button size="sm" variant="outline-primary" disabled={busy || unsaved} onClick={() => download('csv')}>Export CSV</Button>
      </div>
      {bom.part_mapping?.secondary_column && <div className="px-3 py-2 border-bottom">
        <Form.Check type="switch" id="source-row-view" label="Show original source rows (flat editing view)"
          checked={sourceView} disabled={busy || editingCell}
          onChange={event => { setSourceView(event.target.checked); setExpanded(null); }} />
        <small className="text-muted">{grouped
          ? 'Click a primary part number to view MPN subrows and edit original source fields. Quantities are not combined. Filters show matching source lines only.'
          : 'Original rows are shown separately, with their original identities and quantities.'}</small>
      </div>}
      <div className="bomwise-worksheet-options">
      <details><summary>Filters &amp; view</summary>
        <div className="bomwise-option-content">
          <Form.Select style={{ maxWidth: 220 }} aria-label="Filter column" value={filterColumn} onChange={e => setFilterColumn(e.target.value)}>
            <option value="">All columns</option>{bom.columns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Form.Select>
          <Form.Control style={{ maxWidth: 250 }} aria-label="Filter value" placeholder="Contains…" value={filterValue} onChange={e => setFilterValue(e.target.value)} />
          <Button variant="outline-secondary" onClick={() => { setSearch(''); setFilterColumn(''); setFilterValue(''); setGridFilter({ items: [] }); setEditableOnly(false); }}>Clear filters</Button>
          {showPermissions && !!policy.write_columns.length && <Form.Check type="switch" id="editable-rows-only"
            label="Editable rows only" checked={editableOnly} onChange={event => setEditableOnly(event.target.checked)} />}
        </div>
      </details>
      <details><summary>Worksheet tools &amp; export</summary>
        <div className="bomwise-option-content">
          {policy.create_columns && <Button disabled={busy || unsaved || bom.column_count >= 100} onClick={() => setColumnDialog(true)}>Add column</Button>}
          {policy.delete_columns && <>
            <Form.Select style={{ maxWidth: 220 }} aria-label="Column to delete" value={deleteColumn} onChange={e => setDeleteColumn(e.target.value)}>
              <option value="">Choose column to delete</option>
              {bom.columns.filter(c => policy.write_columns.includes(c.id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Form.Select>
            <Button variant="outline-danger" disabled={busy || unsaved || !deleteColumn} onClick={async () => {
              const name = bom.columns.find(c => c.id === deleteColumn)?.name;
              if (window.confirm(`Delete “${name}” and all its cells from this BOM?`)) {
                if (await perform('delete_column', { column_id: deleteColumn })) { setDeleteColumn(''); setFilterColumn(''); }
              }
            }}>Delete column</Button>
          </>}
          <Button variant="outline-primary" disabled={busy || unsaved} onClick={() => download('xlsx')}>Export Excel</Button>
          <Form.Check label="Only filtered rows" checked={exportFiltered} onChange={e => setExportFiltered(e.target.checked)} />
          {busy && <Spinner size="sm" />}
        </div>
      </details>
      </div>
      <div className="bomwise-edit-bar">
        <span>{policy.write_columns.length ? (grouped ? 'Expand a primary part number, then open Source fields to edit. Save changes applies all drafts.'
          : 'Use a row pencil or double-click a cell to edit. Enter keeps a draft; Escape cancels the cell.') : 'You have read-only access to the visible fields.'}</span>
        {!!policy.write_columns.length && <>
          <Button size="sm" disabled={busy || !unsaved} onMouseDown={event => event.preventDefault()} onClick={saveDrafts}>Save changes</Button>
          <Button size="sm" variant="outline-secondary" disabled={busy || !unsaved} onMouseDown={event => event.preventDefault()} onClick={discardDrafts}>Discard changes</Button>
        </>}
      </div>
      <div className="bomwise-grid-container">
        <ExpandedRowContext.Provider value={{ expanded,
          detailLabel: grouped ? row => `Mapped MPNs for ${row.data[bom.part_mapping.primary_column] || 'missing primary part number'}` : undefined,
          renderDetail: row => grouped ? <MappedPartDetail key={row.line_item_id} bom={bom} group={row}
            busy={busy} locked={unsaved} onStage={stageRow} onChanged={load} onMutating={setBusy} />
            : <QuoteComparison bom={bom} row={row} locked={unsaved} onChanged={load} onMutating={setBusy} /> }}>
        <DataGrid rows={displayRows} getRowId={row => row.line_item_id} columns={columns}
          aria-label={`${bom.name} line items`}
          apiRef={apiRef}
          onCellEditStart={({ id, field }) => { activeCellRef.current = { id, field }; setEditingCell(true); }}
          onCellEditStop={() => { activeCellRef.current = null; setEditingCell(false); }}
          className={showPermissions ? 'bomwise-permission-grid' : undefined}
          getCellClassName={({ row, field }) => showPermissions && field.startsWith('cell:')
            ? (cellEditable(row, field) ? 'bomwise-cell--editable' : 'bomwise-cell--readonly') : ''}
          isCellEditable={({ row, field }) => !busy && cellEditable(row, field)}
          density="compact" disableRowSelectionOnClick loading={busy}
          columnHeaderHeight={showPermissions ? 52 : 36} rowHeight={34}
          getRowHeight={getRowHeight}
          onRowClick={({ row }, event) => {
            if (busy || event.target.closest('button,input,select,a,.MuiDataGrid-cell--editing')
                || (!grouped && row.quote_access === false) || !policy.read_columns.length || event.detail > 1) return;
            setExpanded(previous => previous === row.line_item_id ? null : row.line_item_id);
          }}
          pageSizeOptions={[10, 25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          slots={{ toolbar: Toolbar, row: ExpandableBOMRow }} filterMode="server" filterModel={gridFilter} onFilterModelChange={setGridFilter}
          processRowUpdate={stageRow}
          onProcessRowUpdateError={failure => setError(message(failure))}
          localeText={{ noRowsLabel: editableOnly ? 'No editable rows match your current filters'
            : bom.items.length ? 'No rows match your filters' : 'No rows yet — add a row to get started' }}
        />
        </ExpandedRowContext.Provider>
      </div>
      <div className="bomwise-worksheet-footer"><span>{grouped ? `${displayRows.length} primary parts · ` : ''}{visibleRows.length} of {bom.items.length} source rows
        {showPermissions && ` · ${visibleEditableRowCount} shown rows have editable cells`}
        {editableOnly && ' · Showing editable rows only'}</span><span>Changes save only when you choose Save changes</span></div>
      </section>
    </>}
    {share && <ShareBOM bom={bom} onHide={() => setShare(false)} onChange={load} />}
    <Modal show={detailsDialog} onHide={() => !busy && setDetailsDialog(false)}>
      <Form onSubmit={async e => {
        e.preventDefault();
        if (!detailsName.trim() || detailsName.trim().length > 160 || detailsDescription.length > 2000) return;
        if (detailsMapping && !validMapping(detailsMapping, bom.columns)) return;
        setBusy(true); setError('');
        try {
          await apiService.updateBOMMetadata(bomId, bom.revision, {
            name: detailsName.trim(), description: detailsDescription.trim(),
            ...(ready && (detailsMapping || bom.part_mapping) ? { part_mapping: detailsMapping } : {})
          });
          setExpanded(null);
          await load(); setDetailsDialog(false);
        } catch (failure) { setError(message(failure)); }
        finally { setBusy(false); }
      }}>
        <Modal.Header closeButton><Modal.Title>Edit BOM details</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error} Reload the BOM before retrying a conflict; your draft is kept open.</Alert>}
          <Form.Group className="mb-3" controlId="details-bom-name"><Form.Label>BOM name</Form.Label>
            <Form.Control required maxLength={160} value={detailsName} disabled={busy} onChange={e => setDetailsName(e.target.value)} /></Form.Group>
          <Form.Group controlId="details-bom-description"><Form.Label>Description (optional)</Form.Label>
            <Form.Control as="textarea" rows={3} maxLength={2000} value={detailsDescription} disabled={busy} onChange={e => setDetailsDescription(e.target.value)} /></Form.Group>
          {ready && <div className="mt-3">
            <Form.Check type="switch" id="configure-part-mapping" label="Configure part-number layout"
              checked={!!detailsMapping} disabled={busy}
              onChange={event => setDetailsMapping(event.target.checked ? emptyMapping() : null)} />
            {detailsMapping && <PartMappingFields id="details-part-mapping" columns={bom.columns}
              value={detailsMapping} onChange={setDetailsMapping} disabled={busy}
              rows={bom.items.slice(0, 10)} />}
          </div>}
          <Button className="mt-3" variant="outline-secondary" disabled={busy} onClick={() => load().then(() => setError('')).catch(e => setError(message(e)))}>Reload BOM (keep draft)</Button>
        </Modal.Body>
        <Modal.Footer><Button type="submit" disabled={busy || !detailsName.trim() || (!!detailsMapping && !validMapping(detailsMapping, bom.columns))}>Save details</Button></Modal.Footer>
      </Form>
    </Modal>
    <Modal show={columnDialog} onHide={() => setColumnDialog(false)}>
      <Form onSubmit={async e => {
        e.preventDefault();
        if (await perform('create_column', { name: columnName })) { setColumnName(''); setColumnDialog(false); }
      }}>
        <Modal.Header closeButton><Modal.Title>Add column</Modal.Title></Modal.Header>
        <Modal.Body><Form.Control autoFocus required maxLength={120} aria-label="New column name" placeholder="Column name" value={columnName} onChange={e => setColumnName(e.target.value)} /></Modal.Body>
        <Modal.Footer><Button type="submit" disabled={busy}>Create column</Button></Modal.Footer>
      </Form>
    </Modal>
  </div>;
}
